import { RadarFigure } from "./RadarFigure";
import { STEM_NAMES, type TenGodStructures, type TenGodTotals } from "./types";

const HEADERS = (
  <>
    <th rowSpan={2} scope="col" />
    {["Hour", "Day", "Month", "Year"].map((name) => (
      <th key={name} colSpan={2} scope="colgroup">
        {name}
      </th>
    ))}
    <th colSpan={2} scope="colgroup">Composition</th>
    <th colSpan={3} scope="colgroup">Predominant</th>
    <th scope="col" />
  </>
);

interface BaseProps {
  base: "year" | "month" | "day" | "hour";
  branchName: string;
  data: TenGodStructures;
}

/** One of the four Ten God tables: every stem's share, read from one branch of the chart. */
export function TenGodBaseSection({ base, branchName, data }: BaseProps) {
  return (
    <section className="bazi-section">
      <p className="bazi-note">{`Analisa dengan EB ${base} (${branchName})`}</p>
      <div className="bazi-side-by-side">
        <div className="bazi-scroll">
          <table className="bazi-table" aria-label={`Ten God by ${base} branch`}>
            <thead>
              <tr>{HEADERS}</tr>
              <tr>
                {[0, 1, 2, 3].flatMap((i) => [
                  <th key={`hs${i}`} scope="col">HS</th>,
                  <th key={`eb${i}`} scope="col">EB</th>,
                ])}
                <th scope="col">Abs</th>
                <th scope="col">%</th>
                <th scope="col" />
                <th scope="col">Abs</th>
                <th scope="col">%</th>
                <th scope="col">Basic</th>
              </tr>
            </thead>
            <tbody>
              {STEM_NAMES.map((stem) => {
                const row = data[stem]!;
                return (
                  <tr key={stem}>
                    <th scope="row">{`${stem} - ${row.tenGod}`}</th>
                    <td>{row.hourPillar.hs}</td>
                    <td>{row.hourPillar.eb}</td>
                    <td>{row.dayPillar.hs}</td>
                    <td>{row.dayPillar.eb}</td>
                    <td>{row.monthPillar.hs}</td>
                    <td>{row.monthPillar.eb}</td>
                    <td>{row.yearPillar.hs}</td>
                    <td>{row.yearPillar.eb}</td>
                    <td>{row.composition}</td>
                    <td>{row.compositionPercentage} %</td>
                    <td>{row.seasonWeight}</td>
                    <td>{row.predominant}</td>
                    <td>{row.predominantPercentage} %</td>
                    <td>{row.basic}</td>
                  </tr>
                );
              })}
              <tr aria-label="Total">
                <th scope="row" colSpan={10} />
                <td>{data.totalComposition}</td>
                <td>100%</td>
                <td />
                <td>{data.totalPredominant}</td>
                <td>100%</td>
                <td>{data.totalBasic}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <RadarFigure
          label={`Ten God ${base} chart`}
          series="Ten God"
          labels={data.chartData.labels}
          data={data.chartData.data}
        />
      </div>
    </section>
  );
}

/** The overall Ten God table: each pillar's basic score added up across the four bases. */
export function TenGodTotalSection({ data }: { data: TenGodTotals }) {
  return (
    <section className="bazi-section">
      <p className="bazi-note">Total</p>
      <div className="bazi-side-by-side">
        <div className="bazi-scroll">
          <table className="bazi-table" aria-label="Ten God total">
            <thead>
              <tr>
                <th scope="col" />
                {["HP", "DP", "MP", "YP"].map((name) => (
                  <th key={name} scope="col">{name}</th>
                ))}
                <th colSpan={2} scope="colgroup">Composition</th>
                <th colSpan={3} scope="colgroup">Predominant</th>
              </tr>
            </thead>
            <tbody>
              {STEM_NAMES.map((stem) => {
                const row = data[stem]!;
                return (
                  <tr key={stem}>
                    <th scope="row">{`${stem} - ${row.tenGod}`}</th>
                    <td>{row.hourBasicTotal}</td>
                    <td>{row.dayBasicTotal}</td>
                    <td>{row.monthBasicTotal}</td>
                    <td>{row.yearBasicTotal}</td>
                    <td>{row.composition}</td>
                    <td>{row.compositionPercentage} %</td>
                    <td>{row.seasonWeight}</td>
                    <td>{row.predominant}</td>
                    <td>{row.predominantPercentage} %</td>
                  </tr>
                );
              })}
              <tr>
                <th scope="row" colSpan={5} />
                <td>{data.totalComposition}</td>
                <td>100%</td>
                <td />
                <td>{data.totalPredominant}</td>
                <td>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <RadarFigure label="Ten God total chart" series="Ten God" labels={data.chartData.labels} data={data.chartData.data} />
      </div>
    </section>
  );
}
